// WelcomeDialog.js
import React from "react";
import {
  Dialog,
  Button,
  Typography,
  Box,
  Avatar,
  IconButton,
  Fade,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import WavingHandIcon from "@mui/icons-material/WavingHand";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "20px",
    maxWidth: "500px",
    width: "90%",
    overflow: "hidden",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background:
        "linear-gradient(135deg, rgba(102, 126, 234, 0.95) 0%, rgba(118, 75, 162, 0.95) 100%)",
      zIndex: 1,
    },
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 2,
  textAlign: "center",
  padding: theme.spacing(4),
  paddingTop: theme.spacing(5),
  background: "transparent",
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 80,
  height: 80,
  margin: "0 auto",
  marginBottom: theme.spacing(2),
  backgroundColor: "rgba(255, 255, 255, 0.2)",
  fontSize: "2rem",
  fontWeight: "bold",
  border: "3px solid rgba(255, 255, 255, 0.3)",
  boxShadow: "0 8px 25px rgba(0, 0, 0, 0.3)",
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(1),
  top: theme.spacing(1),
  color: "rgba(255, 255, 255, 0.7)",
  zIndex: 3,
  "&:hover": {
    color: "white",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
  },
}));

const StartButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(45deg, #FF6B6B, #4ECDC4)",
  color: "white",
  padding: theme.spacing(1.5, 4),
  fontSize: "1.1rem",
  fontWeight: "bold",
  borderRadius: "25px",
  textTransform: "none",
  boxShadow: "0 8px 25px rgba(0, 0, 0, 0.3)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 12px 30px rgba(0, 0, 0, 0.4)",
    background: "linear-gradient(45deg, #FF5252, #26A69A)",
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: theme.spacing(2),
  "& .logo-text": {
    fontSize: "1.5rem",
    fontWeight: "bold",
    marginLeft: theme.spacing(1),
    background: "linear-gradient(45deg, #FFD700, #FFA500)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },
}));

const WelcomeDialog = ({ open, onClose, onStart, user }) => {
  const getDisplayName = () => {
    // Безопасная проверка на существование данных пользователя
    if (!user) return "User";

    // Если есть firstName и lastName, используем их
    if (user.firstName && user.lastName) {
      const firstName = user.firstName.trim();
      const lastName = user.lastName.trim();
      if (firstName.length > 0 && lastName.length > 0) {
        return `${firstName} ${lastName}`;
      }
    }

    // Если есть только firstName
    if (user.firstName && user.firstName.trim().length > 0) {
      return user.firstName.trim();
    }

    // Если есть только lastName
    if (user.lastName && user.lastName.trim().length > 0) {
      return user.lastName.trim();
    }

    // Если есть login, используем его
    if (user.login && user.login.trim().length > 0) {
      return user.login.trim();
    }

    // Если есть email, используем его
    if (user.email && user.email.trim().length > 0) {
      return user.email.trim();
    }

    // Дефолтное значение
    return "User";
  };

  const getInitials = () => {
    // Безопасная проверка на существование данных пользователя
    if (!user) return "U";

    // Если есть firstName и lastName, используем их
    if (user.firstName && user.lastName) {
      const firstName = user.firstName.trim();
      const lastName = user.lastName.trim();
      if (firstName.length > 0 && lastName.length > 0) {
        return firstName[0].toUpperCase() + lastName[0].toUpperCase();
      }
    }

    // Если есть только firstName
    if (user.firstName && user.firstName.trim().length > 0) {
      return user.firstName.trim()[0].toUpperCase();
    }

    // Если есть только lastName
    if (user.lastName && user.lastName.trim().length > 0) {
      return user.lastName.trim()[0].toUpperCase();
    }

    // Если есть login, используем его
    if (user.login && user.login.trim().length > 0) {
      const login = user.login.trim();
      // Если login содержит пробелы, берем первые буквы слов
      const loginParts = login.split(" ");
      if (loginParts.length >= 2) {
        return loginParts[0][0].toUpperCase() + loginParts[1][0].toUpperCase();
      }
      return login[0].toUpperCase();
    }

    // Если есть email, используем его
    if (user.email && user.email.trim().length > 0) {
      return user.email.trim()[0].toUpperCase();
    }

    // Дефолтное значение
    return "U";
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      transitionDuration={500}
    >
      <CloseButton onClick={onClose}>
        <CloseIcon />
      </CloseButton>

      <ContentWrapper>
        <LogoContainer>
          <StarIcon sx={{ fontSize: "2rem", color: "#FFD700" }} />
          <Typography className="logo-text">Mailsball</Typography>
        </LogoContainer>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <WavingHandIcon sx={{ fontSize: "2rem", mr: 1, color: "#FFD700" }} />
          <Typography variant="h4" sx={{ fontWeight: "bold", color: "white" }}>
            Welcome!
          </Typography>
        </Box>

        <StyledAvatar>{getInitials()}</StyledAvatar>

        <Typography
          variant="h5"
          sx={{ mb: 2, fontWeight: "bold", color: "white" }}
        >
          Hello, {getDisplayName()}!
        </Typography>

        <Typography
          variant="body1"
          sx={{ mb: 3, color: "rgba(255, 255, 255, 0.9)", lineHeight: 1.6 }}
        >
          Welcome to Mailsball! We're excited to have you on board. Let's get
          you started with a quick setup to unlock the full potential of our
          email & SMS marketing platform.
        </Typography>

        <Typography
          variant="body2"
          sx={{ mb: 4, color: "rgba(255, 255, 255, 0.8)", fontStyle: "italic" }}
        >
          It only takes a few minutes to set up, and you'll be ready to send
          your first campaign!
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <StartButton onClick={onStart} variant="contained">
            Get Started
          </StartButton>
          <Button
            onClick={onClose}
            sx={{
              color: "rgba(255, 255, 255, 0.8)",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                color: "white",
              },
            }}
          >
            Skip for now
          </Button>
        </Box>
      </ContentWrapper>
    </StyledDialog>
  );
};

export default WelcomeDialog;
